import React, { useEffect, useState, useContext } from "react";
import _ from "lodash";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  Grid,
  TextField,
  Typography,
  TablePagination,
} from "@mui/material";
import MoreMenu from "./MoreMenu.js";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import { applySortFilter } from "../../utils/tableFunctions";
import {
  getPaymentHosts,
  paymentHostAction,
  getActivePaymentBrands,
  getCurrencyList,
  getPaymentHostDetails,
  getPaymentHostFilterConditions,
} from "../../services/payment";
import { SingleSelectType, MultiSelect } from "../table/Filter.js";
import { PaymentHostContext } from "./PaymentHostSwitchView.js";
import { Log } from "../../services/services.js";
import * as Yup from "yup";
import { useFormik } from "formik";
import { StyledTableRow } from "../TableRow.js";
import FormSearch from "../FormSearch.js";
import CircularLoader from "../circularLoader.js";
import FormDialog from "../FormDialog.js";
import { GREY } from "../../theme/palette.js";
import Label from "../Label.js";
import CommonDialog from "../CustomDialog.js";
import HeaderHelpPage from "../HeaderHelpPage.js";
import PaymentHostDetails from "../../pages/details/admin/PaymentHostDetails.js";
import ConfigurationParams, {
  emptyConfigParam,
  validateConfigParams,
} from "./ConfigurationParams.js";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "name", label: "Payment Host" },
  { id: `payment_brand`, label: "Payment Brand" },
  { id: "is_active", label: "Active" },
  { id: "menu" },
];

const TYPE = {
  REMOVE: "REMOVE",
  RESTORE: "RESTORE",
};

function PaymentHost({ userRole }) {
  const { state, dispatch } = useContext(PaymentHostContext);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("name");
  const [loaded, setLoaded] = useState(false);
  const [reset, setReset] = useState(false);

  const [paymentHosts, setPaymentHosts] = useState({ results: [], count: 0 });
  const [allowedCurrency, setAllowedCurrency] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [currentFilter, setCurrentFilter] = useState(defaultValue);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
  });

  //dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [itemId, setItemId] = useState(null);

  const CustomTableCell = ({ align, field, id }) => {
    return (
      <TableCell
        align={align}
        onClick={() => {
          // window.open(`/3/app/payment-host-details/${id}`, "_blank");
          setItemId(id)
        }}
      >
        {field}
      </TableCell>
    );
  };

  const handleCloseDialog = () => {
    openEditDialog && setOpenEditDialog(false);
    openDeleteDialog && setOpenDeleteDialog(false);
    openRestoreDialog && setOpenRestoreDialog(false);
    selectedId && setSelectedId()
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPaymentHosts({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: { ...currentFilter, ...queryParams },
    });
    setPagination({
      page: queryParams.page,
      page_size: queryParams.page_size,
    });
  };

  const filteredList = applySortFilter(
    paymentHosts.results || [],
    order,
    orderBy
  );

  const isItemNotFound = (paymentHosts.results || []).length === 0;

  const DeleteHostDialog = ({
    open,
    selectedId,
    setResponse,
    setLoaded,
    type,
    onClose,
  }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: Yup.object().shape({
        reason: Yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        await paymentHostAction(type, { selectedId, reason: values.reason }, () =>
          dispatch({ type: "FETCHING_PAYMENT_HOST" })
        );
        handleClose();
      }
    });
  
    const handleClose = () => {
      onClose();
      resetForm();
    };

    return (
        <FormDialog
          open={open}
          onClose={handleClose}
          title={`${_.capitalize(type === TYPE.REMOVE ? "DELETE" : type)} this Payment Host?`}
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          maxWidth="sm"
        >
          <Grid container sx={{ alignItems: "center" }} spacing={3}>
            <Grid item md={5} xs={5}>
              <Typography variant="subtitle1">Reason *</Typography>
            </Grid>
            <Grid item md={7} xs={7}>
              <TextField
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Please enter your reason"
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </FormDialog>
    );
  };

  const EditHostDialog = ({ open, selectedId, setResponse, setLoaded, onClose }) => {
    const { dispatch } = useContext(PaymentHostContext);
    const [currencyList, setCurrencyList] = useState([]);
    const [paymentBrands, setPaymentBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState();
    const [paymentHost, setPaymentHost] = useState("");
    const [hostDetails, setHostDetails] = useState();
    const [formFields, setFormFields] = useState([]);
    const [configErrors, setConfigErrors] = useState([]);

    const [selectAllowedCurrencyList, setSelectAllowedCurrencyList] = useState(
      []
    );

    useEffect(() => {
      getActivePaymentBrands(setPaymentBrands, setLoaded);

      let editPaymentHost = (paymentHosts.results || []).find((e) => e.id === selectedId);

      setFieldValue("payment_brand", editPaymentHost.payment_brand_id)
      setFieldValue("paymentHost", editPaymentHost.name)

      if (!hostDetails) {
        getPaymentHostDetails(selectedId, setHostDetails, setLoaded);
      }
      if (hostDetails) {
        let idList = [];
        hostDetails.currency.forEach((h) => {
          idList.push(h.id);
        });
        setFieldValue("currency_list", idList)
        let list = [];
        allowedCurrency.forEach((method) => {
          if (!selectAllowedCurrencyList.includes(method.id)) {
            list.push(method);
          }
        });

        setCurrencyList(list);
        for (const [key, value] of Object.entries(
          JSON.parse(hostDetails.data)
        )) {
          setFormFields((prevState) => [
            ...prevState,
            { param: key, val: value },
          ]);
        }
      }
    }, [hostDetails]);

    useEffect(() => {
      if (reset) setReset(false);
    }, [reset]);

    const handleFormChange = (event, index) => {
      let data = [...formFields];
      data[index][event.target.name] = event.target.value;
      setFormFields(data);

      let fieldErrors = [...configErrors];
      fieldErrors[index] = { ...fieldErrors[index], [event.target.name]: "" };
      setConfigErrors(fieldErrors);
    };

    const addFields = () => {
      setFormFields([...formFields, emptyConfigParam()]);
      setConfigErrors([...configErrors, {}]);
    };

    const removeFields = (index) => {
      let data = [...formFields];
      data.splice(index, 1);
      setFormFields(data);

      let fieldErrors = [...configErrors];
      fieldErrors.splice(index, 1);
      setConfigErrors(fieldErrors);
    };

    const {
      handleChange,
      handleSubmit,
      handleBlur,
      errors,
      values,
      touched,
      isSubmitting,
      resetForm,
      setFieldValue,
    } = useFormik({
      initialValues: {
        paymentHost: "",
        payment_brand: "",
        currency_list: []
      },
      validationSchema: Yup.object().shape({
        paymentHost: Yup.string().required("This field is required"),
        payment_brand: Yup.string().required("This field is required"),
        currency_list: Yup.array().min(1, "This field is required"),
      }),
      onSubmit: async (values) => {
        const { errors: paramErrors, hasError } = validateConfigParams(formFields);
        setConfigErrors(paramErrors);
        if (hasError) return;

        const paymentHost = values.paymentHost.trim();
        const data = {};
        formFields.forEach((field) => (data[field.param.trim()] = field.val));
        Log.info(`Click button [Submit] - Update Payment Host details by Id: ${selectedId}`, {
          name: paymentHost,
          payment_brand: values.payment_brand,
          currency_list: values.currency_list,
          data: JSON.stringify(data),
        })
        await paymentHostAction(
          "update",
          {
            selectedId: selectedId,
            name: paymentHost,
            payment_brand: values.payment_brand,
            currency_list: values.currency_list,
            data: JSON.stringify(data),
          },
          () => dispatch({ type: "FETCHING_PAYMENT_HOST" })
        );
        setOpenEditDialog(false);
      },
    });

    // Formik only reaches onSubmit once the Yup schema passes, so the config
    // params are validated here as well to surface their errors on every Submit.
    const handleFormSubmit = (event) => {
      const { errors: paramErrors } = validateConfigParams(formFields);
      setConfigErrors(paramErrors);
      handleSubmit(event);
    };

    return (
      <FormDialog
        open={open}
        onClose={onClose}
        title="Edit Payment Host"
        handleSubmit={handleFormSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        {loaded && (
          <SingleSelectType
            name="payment_brand"
            id="true"
            label="Payment Brand"
            options={paymentBrands.map((method) => method)}
            selected={values.payment_brand}
            setSelected={data => setFieldValue("payment_brand", data)}
            size="small"
            error={Boolean(touched.payment_brand && errors.payment_brand)}
            helperText={touched.payment_brand && errors.payment_brand}
          />
        )}
        <TextField
          name="paymentHost"
          label="Name of payment Host"
          fullWidth
          variant="standard"
          value={values.paymentHost}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.paymentHost && errors.paymentHost)}
          helperText={touched.paymentHost && errors.paymentHost}
          disabled={isSubmitting}
          sx={{ marginBottom: 4 }}
        />
        {loaded && (
          <>
            <Typography sx={{ pb: 0.5, color: GREY[600], fontSize: 12 }}>
              Currency
            </Typography>
            <MultiSelect
              name="currency_list"
              options={allowedCurrency.map((method) => ({
                value: method.id ? method.id : "",
                label: method.currency ? method.currency : "",
              }))}
              selectedValues={values.currency_list}
              setSelectedValues={data => setFieldValue("currency_list", data)}
              errorMsg={touched.currency_list && errors.currency_list}
            />
          </>
        )}
        <ConfigurationParams
          formFields={formFields}
          errors={configErrors}
          onChange={handleFormChange}
          onAdd={addFields}
          onRemove={removeFields}
          disabled={isSubmitting}
        />
      </FormDialog>
    );
  };

  const renderData = data => {
    if (!data || !data.results) return;
    const newData = data.results.map(item => ({
      ...item,
      payment_brand: item.payment_brand?.name,
      payment_brand_id: item.payment_brand?.id
    }))
    setPaymentHosts({ count: data.count, results: newData });
  }

  useEffect(() => {
    getPaymentHosts({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: { ...defaultValue, ...pagination }
    });
    getPaymentHostFilterConditions(setHostFilter, setFLoaded);
    getCurrencyList(setAllowedCurrency, setLoaded);
  }, []);

  useEffect(() => {
    if (state.fetching_payment_host) {
      getPaymentHosts({
        setResponse: renderData,
        setLoaded: setLoaded,
        filter: { ...currentFilter, ...pagination }
      });
      dispatch({ type: "FETCHED_PAYMENT_HOST" });
    }
  }, [state.fetching_payment_host]);

  // filter
  const [hostFilter, setHostFilter] = useState("");
  const [fLoaded, setFLoaded] = useState(false);
  const onFilterSubmit = (fParams) => {
    const newParams = {
      ...fParams,
      name: fParams.name?.label,
      payment_brand: fParams.payment_brand?.id,
      name_input: inputValues?.name?.trim() || null,
      payment_brand_input: inputValues?.payment_brand?.trim() || null,
    }
    if (newParams.is_active === 'all') delete newParams.is_active;
    currentFilter && setCurrentFilter(newParams);
    Log.info('Click button [Apply Filter] - Search Payment Host', newParams);
    setLoaded(false);
    queryPage({ ...newParams, page: 1, page_size: pagination.page_size });
  };

  const schema = [
    {
      type: "search-select",
      label: "Payment Host",
      param: "name",
      options: hostFilter?.name?.map((host) => ({
        label: host.name,
        id: host.id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Payment Brand",
      param: "payment_brand",
      options: hostFilter?.payment_brand?.map((brand) => ({
        label: brand.name,
        id: brand.id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "select",
      label: "Active",
      param: "is_active",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map(({ id, name, payment_brand, is_active }) => {
                      return (
                        <StyledTableRow
                          hover
                          key={id}
                        >
                          <CustomTableCell field={name} id={id} />
                          <CustomTableCell
                            field={payment_brand}
                            id={id}
                          />
                          <CustomTableCell
                            field={
                              <Label color={is_active ? "success" : "error"}>
                                {is_active ? "YES" : "NO"}
                              </Label>
                            }
                            id={id}
                          />
                          <TableCell align="right">
                            {is_active && (userRole.includes("Edit") ||
                              userRole.includes("Delete")) && (
                              <MoreMenu
                                setOpenDeleteDialog={setOpenDeleteDialog}
                                setOpenEditDialog={setOpenEditDialog}
                                setOpenRestoreDialog={setOpenRestoreDialog}
                                setRow={() => setSelectedId(id)}
                                userRole={userRole}
                                itemActions={[is_active ? "Delete" : "Restore"]}
                                is_active={is_active}
                                showRestore={true}
                              />
                            )}
                          </TableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound z/>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={paymentHosts?.count || 0}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {(openDeleteDialog || openRestoreDialog) && (
            <DeleteHostDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
              type={openDeleteDialog ? TYPE.REMOVE : TYPE.RESTORE}
            />
          )}
          {openEditDialog && (
            <EditHostDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
        </>
      )}

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Payment Host Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <PaymentHostDetails itemId={itemId} />
        </CommonDialog>
      )}
    </Box>
  );
}

export default PaymentHost;
